import { Box, Card, Heading, Text } from "@chakra-ui/react";
import { useEffect, useRef } from "react";

type Props = {
  title: string;
  subtitle?: string;
  filters?: JSX.Element;
  buttons?: JSX.Element;
  table: JSX.Element;
  footer?: JSX.Element;
  sticky?: boolean;
  /** When this value changes, the page scroll container jumps back to the top. */
  scrollResetKey?: string | number | boolean;
};

const CommonLayout = ({
  title,
  subtitle,
  filters,
  buttons,
  table,
  footer,
  sticky,
  scrollResetKey,
  children,
}: React.PropsWithChildren<Props>): React.ReactNode => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [scrollResetKey]);

  return (
    <Box
      ref={scrollRef}
      p={{
        base: sticky ? "0 1rem" : "1rem",
        md: sticky ? "0 3rem" : "1rem 3rem",
      }}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      gap="1.5rem"
      overflow="auto"
    >
      <Box
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ base: "stretch", md: "center" }}
        bg="white"
        p=".5rem 1rem"
        m={{
          md: sticky ? "0 -3rem" : "-1rem -3rem 0 -3rem",
          base: sticky ? "0 -1rem" : "-1rem -1rem 0 -1rem",
        }}
        borderBottom="1.5px solid #ebecef80"
        gap={4}
        position={sticky ? "sticky" : undefined}
        top={0}
        zIndex={1}
      >
        <Box>
          <Heading size="xl" fontWeight="semibold">
            {title}
          </Heading>
          {subtitle && (
            <Text
              textStyle="xs"
              color="gray.500"
              fontWeight="light"
              display={{ base: "none", md: "block" }}
            >
              {subtitle}
            </Text>
          )}
        </Box>
        <Box
          data-tour="page-actions"
          flex={{ base: 1, md: "0 1 auto" }}
          minW={0}
          display="flex"
          justifyContent={{ base: "stretch", md: "flex-end" }}
        >
          {buttons}
        </Box>
      </Box>
      {filters && (
        <Card.Root
          data-tour="page-filters"
          borderRadius="lg"
          border="none"
          display="flex"
          flexDirection="column"
          gap="1rem"
          boxShadow="rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px"
        >
          <Card.Body p="4">{filters}</Card.Body>
        </Card.Root>
      )}
      {children}
      {table}
      {sticky ? (
        <Box
          position="sticky"
          bottom={0}
          zIndex={1}
          bg="#f8fafc"
          m={{ md: "auto -3rem 0 -3rem", base: "auto -1rem 0 -1rem" }}
          p={{ md: ".5rem 3rem", base: ".5rem 1rem" }}
        >
          {footer}
        </Box>
      ) : (
        footer
      )}
    </Box>
  );
};

export default CommonLayout;
